/** packages/shared-components/src/hooks/useKeyRemapping.ts */

import { useState, useCallback } from "react";
import type { KeyConfig } from "../components/Keyboard/types";
import type { KeyPosition } from "../components/Keyboard";

export interface UseKeyRemappingOptions {
  /** 键盘布局数据 */
  layout: KeyConfig[][] | null;
  /** 改键回调 */
  onRemap?: (
    row: number,
    col: number,
    newKeyCode: number,
    originalKeyCode: number
  ) => void | boolean | Promise<void | boolean>;
  /** 禁用的 keyCode 列表（如未配置指令的宏），选择器选择时也会被拒绝 */
  disabledKeyCodes?: number[];
}

export interface UseKeyRemappingReturn {
  /** 选中的按键 code */
  selectedKey: string | null;
  /** 选中的位置 */
  selectedPosition: { row: number; col: number } | null;
  /** 原始 KeyCode */
  originalKeyCode: number | null;
  /** 处理键盘选择 */
  handleKeyboardSelect: (
    keys: string[],
    positions?: Record<string, KeyPosition>
  ) => void;
  /** 处理拖拽改键 */
  handleKeyDrop: (
    sourceKeyCode: number,
    targetRow: number,
    targetCol: number
  ) => void;
  /** 处理选择器选择 (即时生效) */
  handleSelectorSelect: (keyCode: number) => void;
  /** 清空选择 */
  clearSelection: () => void;
}

function findLayoutKeyByUuid(
  layout: KeyConfig[][],
  uuid: string
): KeyConfig | undefined {
  for (const row of layout) {
    const key = row.find((k) => k.uuid === uuid);
    if (key) return key;
  }
  return undefined;
}

function isRemappableLayoutKey(k: KeyConfig): boolean {
  return (
    k.code !== "__gap__" &&
    k.originalCol !== undefined &&
    k.originalCol >= 0 &&
    k.keyCode !== undefined
  );
}

/**
 * 按固件 originalRow/originalCol 查找（63/68 等 slice 首行后，视觉行号 ≠ layout 下标）。
 * 找不到时再回退 layout[row] 下标，兼容旧调用。
 */
function findLayoutKeyAtPosition(
  layout: KeyConfig[][],
  row: number,
  col: number
): KeyConfig | undefined {
  for (const layoutRow of layout) {
    const hit = layoutRow.find(
      (k) =>
        isRemappableLayoutKey(k) &&
        k.originalRow === row &&
        k.originalCol === col
    );
    if (hit) return hit;
  }
  const layoutRow = layout[row];
  if (!layoutRow) return undefined;
  return layoutRow.find(
    (k) => isRemappableLayoutKey(k) && k.originalCol === col
  );
}

export const useKeyRemapping = ({
  layout,
  onRemap,
  disabledKeyCodes,
}: UseKeyRemappingOptions): UseKeyRemappingReturn => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [originalKeyCode, setOriginalKeyCode] = useState<number | null>(null);

  // 清空选择
  const clearSelection = useCallback(() => {
    setSelectedKey(null);
    setSelectedPosition(null);
    setOriginalKeyCode(null);
  }, []);

  // 处理键盘选择
  const handleKeyboardSelect = useCallback(
    (keys: string[], positions?: Record<string, KeyPosition>) => {
      if (keys.length === 0 || !layout) {
        setSelectedKey(null);
        setSelectedPosition(null);
        setOriginalKeyCode(null);
        return;
      }

      const selectedUuid = keys[0];

      // 过滤 gap
      if (selectedUuid === "__gap__") {
        return;
      }

      setSelectedKey(selectedUuid);

      const keyFromUuid = findLayoutKeyByUuid(layout, selectedUuid);

      if (positions && positions[selectedUuid]) {
        const pos = positions[selectedUuid];
        setSelectedPosition({
          row: keyFromUuid?.originalRow ?? pos.row,
          col: keyFromUuid?.originalCol ?? pos.col,
        });

        // slice 机型 keyPositionMap.row 为视觉行，再试 firmwareRow = visual + 1
        const key =
          keyFromUuid ??
          findLayoutKeyAtPosition(layout, pos.row, pos.col) ??
          findLayoutKeyAtPosition(layout, pos.row + 1, pos.col);
        if (key && key.keyCode !== undefined) {
          setOriginalKeyCode(key.keyCode);
        } else {
          setOriginalKeyCode(null);
        }
      } else if (keyFromUuid) {
        setSelectedPosition({
          row: keyFromUuid.originalRow ?? 0,
          col: keyFromUuid.originalCol ?? 0,
        });
        if (keyFromUuid.keyCode !== undefined) {
          setOriginalKeyCode(keyFromUuid.keyCode);
        } else {
          setOriginalKeyCode(null);
        }
      } else {
        setSelectedPosition(null);
        setOriginalKeyCode(null);
      }
    },
    [layout]
  );

  // 处理拖拽改键
  const handleKeyDrop = useCallback(
    (sourceKeyCode: number, targetRow: number, targetCol: number) => {
      if (!layout) return;

      const targetKey =
        findLayoutKeyAtPosition(layout, targetRow, targetCol) ??
        findLayoutKeyAtPosition(layout, targetRow + 1, targetCol);
      if (!targetKey || targetKey.keyCode === undefined) return;

      // 必须以固件行列写回（slice 机型 drop 可能带视觉行，统一以 KeyConfig 为准）
      const writeRow = targetKey.originalRow ?? targetRow;
      const writeCol = targetKey.originalCol ?? targetCol;
      onRemap?.(writeRow, writeCol, sourceKeyCode, targetKey.keyCode);

      clearSelection();
    },
    [layout, onRemap, clearSelection]
  );

  // 处理选择器选择 - 现在直接触发改键
  const handleSelectorSelect = useCallback(
    async (keyCode: number) => {
      if (!selectedPosition || originalKeyCode === null) {
        return;
      }
      if (disabledKeyCodes?.includes(keyCode)) {
        return;
      }

      const result = await onRemap?.(
        selectedPosition.row,
        selectedPosition.col,
        keyCode,
        originalKeyCode
      );
      if (result !== false) {
        clearSelection();
      }
    },
    [
      selectedPosition,
      originalKeyCode,
      onRemap,
      disabledKeyCodes,
      clearSelection,
    ]
  );

  return {
    selectedKey,
    selectedPosition,
    originalKeyCode,
    handleKeyboardSelect,
    handleKeyDrop,
    handleSelectorSelect,
    clearSelection,
  };
};
